import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Election } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const COMMISSIONER_ROLES = ['ELECTORAL_COMMISSIONER'];

/** GET/POST transitions under /api/v1/m39/elections/:id — ELECTORAL_COMMISSIONER only for every transition. */
export function ElectionDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const navigate = useNavigate();
  const isCommissioner = persona?.roles.some((r) => COMMISSIONER_ROLES.includes(r)) ?? false;
  const electionQ = useApiQuery<Election>(id ? `/api/v1/m39/elections/${id}` : null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);

  if (electionQ.loading) return <Spinner label="Loading election…" />;
  if (electionQ.error) return <ErrorNote message={electionQ.error} />;
  const e = electionQ.data;
  if (!e) return null;

  const transition = async (action: string) => {
    setBusy(action);
    setActionError(null);
    try {
      await api.post(`/api/v1/m39/elections/${e.id}/${action}`);
      electionQ.reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? `${err.message} (${err.code})` : `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  const nextActions: Array<{ action: string; label: string; tone: 'primary' | 'secondary' }> = [];
  if (e.state === 'SCHEDULED') nextActions.push({ action: 'mark-roll-revision', label: 'Open roll revision window', tone: 'secondary' });
  if (e.state === 'SCHEDULED' || e.state === 'ROLL_REVISION') nextActions.push({ action: 'open-nominations', label: 'Open nominations', tone: 'primary' });
  if (e.state === 'NOMINATIONS_OPEN') nextActions.push({ action: 'close-nominations', label: 'Close nominations', tone: 'primary' });
  if (e.state === 'NOMINATIONS_CLOSED') nextActions.push({ action: 'open-polling', label: 'Open polling', tone: 'primary' });
  if (e.state === 'POLLING') nextActions.push({ action: 'close-polling', label: 'Close polling → begin counting', tone: 'primary' });
  if (e.state === 'RESULTS_PUBLISHED') nextActions.push({ action: 'close', label: 'Archive (close) election', tone: 'secondary' });
  const cancellable = (['SCHEDULED', 'ROLL_REVISION', 'NOMINATIONS_OPEN', 'NOMINATIONS_CLOSED', 'POLLING'] as string[]).includes(e.state);

  return (
    <div>
      <PageHeader
        title={e.title}
        subtitle={`${e.type.replaceAll('_', ' ')} · election date ${formatDate(e.electionDate)}`}
        actions={<StatusBadge status={e.state} />}
      />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {isCommissioner && (nextActions.length > 0 || cancellable) ? (
        <div className="card mb-6 flex flex-wrap items-center gap-2 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lifecycle</span>
          {nextActions.map((a) => (
            <button
              key={a.action}
              className={a.tone === 'primary' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => transition(a.action)}
              disabled={busy !== null}
            >
              {busy === a.action ? 'Working…' : a.label}
            </button>
          ))}
          {cancellable ? (
            <button className="btn-danger" onClick={() => setShowCancel(true)} disabled={busy !== null}>
              Cancel election
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Nomination deposit" value={scr(e.depositAmountScr)} />
        <StatCard label="Deposit forfeit threshold" value={`${e.depositForfeitThresholdPct}%`} />
        <StatCard label="Nominations opened" value={formatDateTime(e.nominationsOpenAt)} />
        <StatCard label="Polling opened" value={formatDateTime(e.pollingOpensAt)} />
      </div>

      <div className="card mb-6 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Timeline</h2>
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div>
            <div className="field-label">Nominations close</div>
            {formatDateTime(e.nominationsCloseAt)}
          </div>
          <div>
            <div className="field-label">Polling closes</div>
            {formatDateTime(e.pollingClosesAt)}
          </div>
          <div>
            <div className="field-label">Gazette notice</div>
            {e.gazetteNoticeRef ?? '—'}
          </div>
          <div>
            <div className="field-label">Created</div>
            {formatDateTime(e.createdAt)}
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <button className="card p-4 text-left hover:border-brand-300" onClick={() => navigate(`/elections/nominations?electionId=${e.id}`)}>
          <div className="text-sm font-bold text-slate-800">Nominations →</div>
          <div className="mt-1 text-xs text-slate-500">Candidate filings, eligibility checks, and deposits for this election.</div>
        </button>
        <button className="card p-4 text-left hover:border-brand-300" onClick={() => navigate(`/elections/tallies?electionId=${e.id}`)}>
          <div className="text-sm font-bold text-slate-800">Station tallies →</div>
          <div className="mt-1 text-xs text-slate-500">Submit, verify, and dispute per-station ballot accounting.</div>
        </button>
        <button className="card p-4 text-left hover:border-brand-300" onClick={() => navigate(`/elections/results?electionId=${e.id}`)}>
          <div className="text-sm font-bold text-slate-800">Results →</div>
          <div className="mt-1 text-xs text-slate-500">Certify and publish station/seat/national results.</div>
        </button>
      </div>

      {showCancel ? (
        <CancelModal
          electionId={e.id}
          onClose={() => setShowCancel(false)}
          onDone={() => {
            setShowCancel(false);
            electionQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CancelModal({ electionId, onClose, onDone }: { electionId: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m39/elections/${electionId}/cancel`, { reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancellation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Cancel election" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Court order suspending the poll" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Back
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.trim().length < 3}>
            {busy ? 'Cancelling…' : 'Cancel election'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
